import { useState, useEffect, useCallback, RefObject } from "react";
import debounce from "lodash.debounce";

interface UseDynamicFontSizeProps {
  containerRef: RefObject<HTMLElement>;
  textRef: RefObject<HTMLElement>;
  initialFontSize?: number;
  minFontSize?: number;
}

/**
 * Hook for dynamically adjusting the font size of a single-line (whitespace-nowrap) text element to fit within a container.
 * See the `DynamicTextSize` component as that applies this hook and may satisfy most use cases.
 * @param containerRef - The ref of the container element.
 * @param textRef - The ref of the text element that needs to fit within the container. This should have the `whitespace-nowrap` class.
 * @param initialFontSize - The initial font size (default: 24).
 * @param minFontSize - The minimum font size (default: 12).
 * @returns The computed font size that fits within the container.
 */
export const useDynamicFontSize = ({
  containerRef,
  textRef,
  initialFontSize = 24,
  minFontSize = 12,
}: UseDynamicFontSizeProps) => {
  const [fontSize, setFontSize] = useState(initialFontSize);

  const adjustFontSize = useCallback(() => {
    const textElement = textRef.current;
    const containerElement = containerRef.current;

    if (!textElement || !containerElement) return;

    // Reset to initial font size
    let currentFontSize = initialFontSize;
    textElement.style.fontSize = `${currentFontSize}px`;

    // Check if text overflows the container
    while (
      textElement.scrollWidth > containerElement.offsetWidth &&
      currentFontSize > minFontSize
    ) {
      currentFontSize -= 0.5; // Reduce font size incrementally
      textElement.style.fontSize = `${currentFontSize}px`;
    }

    setFontSize(currentFontSize);
  }, [initialFontSize, minFontSize, textRef, containerRef]);

  // Debounced resize handler
  const debouncedAdjustFontSize = useCallback(debounce(adjustFontSize, 100), [
    adjustFontSize,
  ]);

  useEffect(() => {
    adjustFontSize(); // Run on mount

    window.addEventListener("resize", debouncedAdjustFontSize);

    // Observe text content changes
    const textElement = textRef.current;
    if (textElement) {
      const observer = new MutationObserver(debouncedAdjustFontSize);
      observer.observe(textElement, {
        childList: true,
        characterData: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }

    return () => {
      window.removeEventListener("resize", debouncedAdjustFontSize);
      debouncedAdjustFontSize.cancel(); // Clean up debounced function
    };
  }, [debouncedAdjustFontSize, textRef]);

  return fontSize;
};
